export const cleanName = (name: string | undefined | null): string => {
  if (!name) return ""
  return name
    .replace(/&quot;/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/''/g, '')
    .replace(/"/g, '')
    .replace(/'/g, '')
    .trim()
}

// A product's place in the taxonomy, as the catalogue API sends it:
// itemType -> classification -> subClassification -> category -> subCategory.
//
// SUB_CATEGORY rows are spec values ("1300W", "250L") and the "Задгай" loose
// bucket, and the SAME row is reused under dozens of unrelated categories. They
// are therefore never matched by id — only through a key scoped to the
// product's OWN category name.
export const subCategoryKey = (categoryName?: string | null, subCategoryName?: string | null): string =>
  cleanName(categoryName) + "|||" + cleanName(subCategoryName)

// The one place that decides whether a product belongs to the category
// selection. It used to be written out twice — once for the grid and once for
// the brand facet — and the two copies had already drifted apart.
export const matchesCategorySelection = (item: any, idSet: Set<string>, nameSet: Set<string>): boolean => {
  const itemType = item?.itemType
  const classification = item?.classification
  const subClassification = item?.subClassification
  const category = item?.category
  const subCategory = item?.subCategory

  if (itemType && idSet.has(itemType.id)) return true
  if (classification && idSet.has(classification.id)) return true
  if (subClassification && idSet.has(subClassification.id)) return true
  if (category && (idSet.has(category.id) || idSet.has(category.parentId))) return true

  if (classification?.name && nameSet.has(cleanName(classification.name))) return true
  if (subClassification?.name && nameSet.has(cleanName(subClassification.name))) return true
  if (category?.name && nameSet.has(cleanName(category.name))) return true
  if (category?.parent?.name && nameSet.has(cleanName(category.parent.name))) return true

  if (category && subCategory?.name && nameSet.has(subCategoryKey(category.name, subCategory.name))) return true

  return false
}

export const getProductPrice = (product: any, overridePriceMap?: Record<string, number>): number => {
  if (overridePriceMap && product.id && overridePriceMap[product.id] > 0) {
    return overridePriceMap[product.id]
  }
  if (product.price && product.price > 0) {
    return product.price
  }
  // R14 — consignment offering price (supplier B2C from offering) is canonical
  // when no price-service override has been computed yet.
  if (product.isConsignment && product.consignmentSalePrice && product.consignmentSalePrice > 0) {
    return product.consignmentSalePrice
  }
  if (product.standardPrice && product.standardPrice > 0) {
    return product.standardPrice
  }
  if (product.prices && product.prices.length > 0) {
    return product.prices[0]?.price || 0
  }
  return 0
}

// Returns the effective minimum order quantity for a product.
// Must read from inventories[0] to match checkout's warehouseId selection.
export const getMinOrderQuantity = (product: any): number => {
  const val = product?.inventories?.[0]?.minOrderQuantity
  return typeof val === "number" && val > 1 ? val : 1
}

// The one stock threshold for the buyer side (card, table, product page, filter
// legend). It used to be written three times with different cut-offs (>50 green,
// 10-50 yellow, 1-9 red here; another set in the product page), so the same
// quantity read differently from screen to screen.
export const LOW_STOCK_THRESHOLD = 10

export type StockLevel = "ready" | "low" | "out"

export const stockLevel = (stock: number): StockLevel => {
  if (stock >= LOW_STOCK_THRESHOLD) return "ready"
  if (stock > 0) return "low"
  return "out"
}

/** «Нөөцгүй зарах»: a flagged variant may be ordered past available stock (booked as backorder). */
export const sellsWithoutStock = (product: any): boolean =>
  !!(product?.allowSaleWithoutStock || product?.backorderable)

// Filter/view state persisted for this list (sessionStorage). Shared so the
// loading skeleton can lay out in the same view mode the user last chose.
export const LIST_STORAGE_KEY = "buyer-sales.product-list"

export const readStoredViewMode = (): "grid" | "list" => {
  try {
    const raw = sessionStorage.getItem(`${LIST_STORAGE_KEY}.viewMode`)
    return raw === null ? "grid" : (JSON.parse(raw) as "grid" | "list")
  } catch {
    return "grid"
  }
}
